import { POST, UPLOAD } from "./api.js";

/* ============================================================================
 * ขั้นตอนสมัครเป็นพาร์ตเนอร์ (หน้าสาธารณะ /partner)
 *
 * รวมการคุยกับ API ไว้ที่เดียว เพราะฟอร์มถูกแบ่งเป็นหลายคอมโพเนนต์ตามขั้นตอน
 * (PartnerProfile / PartnerVerify / PartnerTerms) แต่ทุกขั้นอ้างถึงชุด endpoint
 * เดียวกัน และมีสิ่งที่ต้องส่งร่วมกันคือ "ตั๋วใบสมัคร" ที่ได้ตอนยืนยัน OTP
 *
 * ตั๋ว (apply_token) เก็บไว้ใน state ของหน้าเท่านั้น ไม่ลง localStorage โดยตั้งใจ —
 * มันแลกเป็นสิทธิ์อัปไฟล์เข้าบักเก็ตของระบบได้ และหน้านี้อาจถูกเปิดบนเครื่องที่ใช้ร่วมกัน
 *
 * ทุกฟังก์ชันคืนค่าที่ GET/POST ของโปรเจกต์คืนมาตรง ๆ ({ status, data, message })
 * ตัว POST เด้งข้อความผิดพลาดให้เองอยู่แล้ว ผู้เรียกจึงเช็กแค่ status ไม่ต้อง alert ซ้ำ
 * ========================================================================== */

/** ขอรหัสยืนยันอีเมล — คืน { expires_in, resend_in, sent, dev_code? } */
export const requestOtp = (email) => POST("partner/otp/request", { email: String(email || "").trim() });

/** ยืนยันรหัส — สำเร็จแล้วได้ { apply_token, email } กลับมา */
export const verifyOtp = (email, code) =>
  POST("partner/otp/verify", { email: String(email || "").trim(), code: String(code || "").trim() });

/**
 * อัปไฟล์ของใบสมัครขึ้น R2
 *
 * ใช้ UPLOAD ตัวเดียวกับที่หน้าหลังบ้านใช้ (ย่อรูป → ขอ presigned URL → PUT ตรงเข้า R2)
 * ต่างแค่ปลายทางที่ออก URL เป็นเส้นสาธารณะที่รับตั๋วแทน Bearer token
 *
 * ไม่ส่ง folder ไปด้วย — ส่งได้แค่ "ชนิด" ของไฟล์ (kind) แล้ว server เป็นคนแปลงเป็น
 * โฟลเดอร์เอง เพื่อไม่ให้ตั๋วใบเดียวเขียนทับโฟลเดอร์ไหนของบักเก็ตก็ได้
 * ค่าที่รับคือ "document" (เอกสารยืนยันตัวตน) กับ "picture" (รูปโปรไฟล์) — ต้องตรงกับ
 * UPLOAD_FOLDERS ใน partner.constant.js ฝั่ง backend
 */
export const uploadDocument = (file, applyToken, kind = "document") =>
  UPLOAD(file, { endpoint: "partner/upload/presign", extra: { apply_token: applyToken, kind } });

/** รูปโปรไฟล์ของผู้สมัคร — คนละโฟลเดอร์กับเอกสารยืนยันตัวตน */
export const uploadPicture = (file, applyToken) => uploadDocument(file, applyToken, "picture");

/** ส่งใบสมัคร — คืน { uid, code, status, submitted_at } */
export const applyPartner = (payload) => POST("partner/apply", payload);

/** ติดตามสถานะใบสมัคร — ต้องรู้ทั้งเลขที่ใบสมัครและอีเมลที่ใช้สมัคร */
export const trackPartner = (code, email) =>
  POST("partner/status", { code: String(code || "").trim(), email: String(email || "").trim() });

/* --------------------------------------------------------- ลืมรหัสผ่าน */
/* ใช้ได้เฉพาะบัญชีพาร์ตเนอร์ — server ปฏิเสธอีเมลของผู้ใช้ภายในไว้ ไม่งั้นคนนอก
   จะขอรหัสตั้งรหัสผ่านใหม่ให้บัญชีผู้ดูแลระบบได้ */

/** ขอรหัสสำหรับตั้งรหัสผ่านใหม่ — คืน { expires_in, resend_in, sent, dev_code? } */
export const forgotPassword = (email) =>
  POST("partner/password/forgot", { email: String(email || "").trim() });

/** ยืนยันรหัส + ตั้งรหัสผ่านใหม่ จบในคำขอเดียว */
export const resetPassword = (email, code, password) =>
  POST("partner/password/reset", {
    email: String(email || "").trim(),
    code: String(code || "").trim(),
    password,
  });

const partnerService = {
  requestOtp,
  verifyOtp,
  uploadDocument,
  uploadPicture,
  applyPartner,
  trackPartner,
  forgotPassword,
  resetPassword,
};

export default partnerService;
